import { ErrorModal } from "@dexteel/mesf-core";
import {
	Autocomplete,
	CircularProgress,
	createFilterOptions,
	TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { FieldError } from "react-hook-form";
import type { ProductForSelector } from "@/shared/models/WorkOrder";
import { getProductsForSelector } from "@/shared/repositories/WorkOrderRepository";

type Props = {
	label: string;
	value: number | null;
	onChange: (value: number | null) => void;
	onProductSelect?: (product: ProductForSelector | null) => void;
	fieldError?: FieldError;
	disabled?: boolean;
	areaAssetId?: number | null;
};

// Selected value shows only the part number (the Extended Part # has its own
// read-only field in the WO modal); the dropdown list and the search use the
// full label.
const getProductLabel = (p: ProductForSelector): string => p.PartNumber;

const getProductOptionLabel = (p: ProductForSelector): string => {
	const extended = p.ExtendedPartNumber ? ` (${p.ExtendedPartNumber})` : "";
	const name = p.GenericName ? ` — ${p.GenericName}` : "";
	const customer = p.CustomerName ? ` — ${p.CustomerName}` : "";
	return `${p.PartNumber}${extended}${name}${customer}`;
};

const filterOptions = createFilterOptions<ProductForSelector>({
	stringify: getProductOptionLabel,
});

export const ProductSelector = ({
	label,
	value,
	onChange,
	onProductSelect,
	fieldError,
	disabled = false,
	areaAssetId = null,
}: Props) => {
	const [error, setError] = useState("");

	const { data: allProducts, isLoading } = useQuery<
		ProductForSelector[],
		Error
	>({
		queryKey: ["productsForSelector", areaAssetId],
		queryFn: () => getProductsForSelector(areaAssetId),
		onError: (e: Error) => setError(e.message),
	});

	const products = allProducts ?? [];

	const selectedProduct = products?.find((p) => p.ProductId === value) || null;

	return (
		<>
			<Autocomplete
				id="product-selector"
				fullWidth
				clearOnEscape
				options={products || []}
				getOptionLabel={getProductLabel}
				filterOptions={filterOptions}
				renderOption={(props, option) => (
					<li {...props} key={option.ProductId}>
						{getProductOptionLabel(option)}
					</li>
				)}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						size="small"
						fullWidth
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading && <CircularProgress color="inherit" size={20} />}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
				value={selectedProduct}
				onChange={(_, newValue) => {
					onChange(newValue?.ProductId ?? null);
					onProductSelect?.(newValue);
				}}
				isOptionEqualToValue={(option, val) =>
					option.ProductId === val.ProductId
				}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading products"
			/>
		</>
	);
};
